import { getSessionId } from 'owa-config/lib/getSessionId';
import { getItem, setItem, removeItem } from 'owa-local-storage';
import { getQueryStringParameters } from 'owa-querystring';

const MARKETING_REDIRECT_SID_KEY = 'marketing_redirect_sid';

/** Known OIDC/MSA prompt values safe to log. */
const KNOWN_PROMPT_VALUES = new Set(['login', 'select_account', 'create_account', 'none']);

// Preserved before handleLoginRedirectPromise strips sessionId from the address bar.
let preservedRedirectSid: string | undefined = undefined;

export function preserveRedirectSid(sid: string | undefined): void {
    preservedRedirectSid = sid;
}

function normalizePrompt(prompt: string | undefined): string | undefined {
    if (!prompt) {
        return undefined;
    }
    return KNOWN_PROMPT_VALUES.has(prompt) ? prompt : 'null';
}

function getStoredRedirectSid(): string | null {
    return getItem(self, MARKETING_REDIRECT_SID_KEY);
}

export function clearMarketingRedirectSid(): void {
    removeItem(self, MARKETING_REDIRECT_SID_KEY);
}

export interface MarketingRedirectLoopInfo {
    isLoop: boolean;
    prompt?: string;
}

/**
 * Checks for a marketing page redirect loop and returns diagnostic info.
 * Should be called before the boot reliability event is sent so the data
 * can be attached via appendMiscData.
 */
export function detectMarketingRedirectLoop(): MarketingRedirectLoopInfo {
    const queryParams = getQueryStringParameters();
    const redirectSid = preservedRedirectSid ?? queryParams.sessionId;
    const storedSid = getStoredRedirectSid();
    // Only flag a loop when the page was reached via a referrer (marketing page or MSA redirect).
    // Session restores, bookmarks, and direct navigation have an empty referrer and are excluded
    // to avoid false positives from stale sessionId + localStorage state.
    const hasReferrer = !!document.referrer;
    const loopDetected = hasReferrer && !!redirectSid && !!storedSid && redirectSid === storedSid;

    return {
        isLoop: loopDetected,
        prompt: normalizePrompt(queryParams.prompt),
    };
}

/**
 * Gets or creates a session ID for the marketing page redirect.
 * If a sessionId is already present in the URL query params, it is reused.
 * Otherwise a new GUID is generated. The sid is stored in localStorage
 * for subsequent loop detection.
 */
export function generateAndStoreRedirectSid(): string {
    const queryParams = getQueryStringParameters();
    const existingSid = queryParams.sessionId;
    const sid = existingSid || getSessionId();
    setItem(self, MARKETING_REDIRECT_SID_KEY, sid);
    return sid;
}
